/**
 * Pure helpers backing the project dashboard (`/projects/{pid}/dashboard`).
 *
 * The dashboard is **current-phase centric**: the source of truth for a
 * migration task's status is its `ExecutionDoc` for the active run, not
 * the task master record. All progress / overdue / workload metrics
 * therefore join `TaskDoc` × `ExecutionDoc` (for the current run) before
 * counting.
 *
 * Everything in this module is pure so the page-level rendering tests
 * can drive deterministic data without spinning up Firestore.
 *
 * Current-run selection mirrors the rest of the app
 * (DagPage / MyTasksPage): prefer the single run with `status === 'active'`,
 * else the first run by `order`. Returning `null` means "the project has
 * no runs yet" — every aggregator handles that gracefully (zero counts).
 */
import {
  primaryBacklogProjectKey,
  readTaskAreaIds,
  type ActivityLogDoc,
  type AssigneeDoc,
  type ExecutionDoc,
  type ExecutionStatus,
  type RunDoc,
  type TaskDoc,
  type WbsCatalogOption,
  type WbsItemDoc,
  type WbsItemStatus,
} from '@mct/shared';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Pick the run we treat as "now" — active wins, else first by order. */
export function pickCurrentRun(runs: readonly RunDoc[]): RunDoc | null {
  if (runs.length === 0) return null;
  const active = runs.find((r) => r.status === 'active');
  if (active) return active;
  const ordered = [...runs].sort((a, b) => a.order - b.order);
  return ordered[0] ?? null;
}

/** Parse an `IsoDate` ("YYYY-MM-DD") into a local-midnight Date. */
function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  // `new Date('2026-05-14')` parses as UTC midnight; we want local
  // midnight so day-boundary math lines up with what a PM sees on their
  // calendar in the runtime's tz.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function endOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(23, 59, 59, 999);
  return out;
}

function diffDaysInclusive(from: Date, to: Date): number {
  // Inclusive of both endpoints: 5/1 → 5/31 is 31 days.
  const a = startOfDay(from).getTime();
  const b = startOfDay(to).getTime();
  return Math.round((b - a) / MS_PER_DAY) + 1;
}

/* -------------------------------------------------------------------- */
/*  A. Current phase overall progress                                   */
/* -------------------------------------------------------------------- */

export interface PhaseProgressBreakdown {
  total: number;
  notStarted: number;
  inProgress: number;
  done: number;
  blocked: number;
  skipped: number;
  /** Count used as the denominator for `donePercent` (excludes skipped). */
  effectiveTotal: number;
  /** 0..100 — `done / effectiveTotal`, rounded. `0` when `effectiveTotal === 0`. */
  donePercent: number;
}

export interface PhaseProgressSummary extends PhaseProgressBreakdown {
  /** Null when there is no current run (project has no runs yet). */
  run: RunDoc | null;
  /** Parsed `plannedStartDate` of the current run, or null if missing. */
  plannedStart: Date | null;
  /** Parsed `plannedEndDate` of the current run, or null if missing. */
  plannedEnd: Date | null;
  /** Total days in the phase (inclusive). `null` when dates missing. */
  totalDays: number | null;
  /**
   * Days elapsed from `plannedStart` to `now` (inclusive, clamped to
   * `[0, totalDays]`). `null` when dates missing.
   */
  elapsedDays: number | null;
  /**
   * Days remaining from `now` to `plannedEnd` (inclusive, clamped to
   * `[0, totalDays]`). `null` when dates missing.
   */
  remainingDays: number | null;
}

/**
 * Walk the project's task master against the executions of the current
 * run and tally status counts. Tasks without a matching execution are
 * treated as `not_started` — the same fallback `joinTasksAndExecutions`
 * uses in the task list view.
 */
export function computeCurrentPhaseProgress(
  tasks: readonly TaskDoc[],
  executions: readonly ExecutionDoc[],
  run: RunDoc | null,
  now: Date,
): PhaseProgressSummary {
  const execByTaskId = new Map<string, ExecutionDoc>();
  for (const e of executions) {
    if (!run || e.runId === run.runId) execByTaskId.set(e.taskId, e);
  }
  let notStarted = 0;
  let inProgress = 0;
  let done = 0;
  let blocked = 0;
  let skipped = 0;
  for (const t of tasks) {
    const status: ExecutionStatus = execByTaskId.get(t.taskId)?.status ?? 'not_started';
    switch (status) {
      case 'not_started':
        notStarted += 1;
        break;
      case 'in_progress':
        inProgress += 1;
        break;
      case 'done':
        done += 1;
        break;
      case 'blocked':
        blocked += 1;
        break;
      case 'skipped':
        skipped += 1;
        break;
    }
  }
  const total = tasks.length;
  const effectiveTotal = total - skipped;
  const donePercent = effectiveTotal > 0 ? Math.round((done / effectiveTotal) * 100) : 0;

  const plannedStart = run ? parseIsoDate(run.plannedStartDate) : null;
  const plannedEnd = run ? parseIsoDate(run.plannedEndDate) : null;
  let totalDays: number | null = null;
  let elapsedDays: number | null = null;
  let remainingDays: number | null = null;
  if (plannedStart && plannedEnd) {
    totalDays = diffDaysInclusive(plannedStart, plannedEnd);
    const today = startOfDay(now);
    const startDay = startOfDay(plannedStart);
    const endDay = startOfDay(plannedEnd);
    elapsedDays = Math.max(
      0,
      Math.min(totalDays, Math.round((today.getTime() - startDay.getTime()) / MS_PER_DAY) + 1),
    );
    remainingDays = Math.max(
      0,
      Math.min(totalDays, Math.round((endDay.getTime() - today.getTime()) / MS_PER_DAY) + 1),
    );
  }

  return {
    total,
    notStarted,
    inProgress,
    done,
    blocked,
    skipped,
    effectiveTotal,
    donePercent,
    run,
    plannedStart,
    plannedEnd,
    totalDays,
    elapsedDays,
    remainingDays,
  };
}

/* -------------------------------------------------------------------- */
/*  B. Today's situation                                                */
/* -------------------------------------------------------------------- */

/**
 * Counts focused on the calendar day surrounding `now`. We don't have a
 * native "scheduled to start/end today" field on migration tasks, so the
 * usable today-signal is:
 *   - `startedToday`: executions whose `startedAt` falls inside today
 *   - `completedToday`: executions whose `completedAt` falls inside today
 *   - `inProgressNow`: currently running (status === in_progress)
 *
 * Surfaces what the PM actually wants at the standup: "how much landed
 * today and what's actively moving right now".
 */
export interface TodaySituation {
  startedToday: number;
  completedToday: number;
  inProgressNow: number;
}

export function computeTodaySituation(
  executions: readonly ExecutionDoc[],
  run: RunDoc | null,
  now: Date,
): TodaySituation {
  if (!run) return { startedToday: 0, completedToday: 0, inProgressNow: 0 };
  const dayStart = startOfDay(now).getTime();
  const dayEnd = endOfDay(now).getTime();
  let startedToday = 0;
  let completedToday = 0;
  let inProgressNow = 0;
  for (const e of executions) {
    if (e.runId !== run.runId) continue;
    if (e.status === 'in_progress') inProgressNow += 1;
    const s = e.startedAt?.toMillis() ?? null;
    if (s != null && s >= dayStart && s <= dayEnd) startedToday += 1;
    const c = e.completedAt?.toMillis() ?? null;
    if (c != null && c >= dayStart && c <= dayEnd) completedToday += 1;
  }
  return { startedToday, completedToday, inProgressNow };
}

/* -------------------------------------------------------------------- */
/*  C. Overdue / risk                                                    */
/* -------------------------------------------------------------------- */

/**
 * "Overdue" definition for migration tasks: the **current run's
 * `plannedEndDate` is in the past** AND the task's execution is still
 * incomplete (`not_started` / `in_progress` / `blocked`). Migration
 * tasks have no per-task due date so the only authoritative deadline is
 * the run's planned end.
 *
 * `daysOverdue` is days from `plannedEndDate` to `now` (inclusive,
 * minimum 1 when overdue). Returned list is sorted with the most
 * overdue first so the UI can show the worst offenders.
 */
export interface OverdueTaskRow {
  taskId: string;
  name: string;
  status: ExecutionStatus;
  assigneeId: string | null;
  /** Days past the phase's `plannedEndDate`, inclusive of the end day. */
  daysOverdue: number;
}

export function computeOverdueTasks(
  tasks: readonly TaskDoc[],
  executions: readonly ExecutionDoc[],
  run: RunDoc | null,
  now: Date,
): OverdueTaskRow[] {
  if (!run) return [];
  const plannedEnd = parseIsoDate(run.plannedEndDate);
  if (!plannedEnd) return [];
  const todayStart = startOfDay(now).getTime();
  const endStart = startOfDay(plannedEnd).getTime();
  if (todayStart <= endStart) return [];
  const daysOverdue = Math.round((todayStart - endStart) / MS_PER_DAY);

  const execByTaskId = new Map<string, ExecutionDoc>();
  for (const e of executions) {
    if (e.runId === run.runId) execByTaskId.set(e.taskId, e);
  }
  const out: OverdueTaskRow[] = [];
  for (const t of tasks) {
    const exec = execByTaskId.get(t.taskId);
    const status: ExecutionStatus = exec?.status ?? 'not_started';
    if (status === 'done' || status === 'skipped') continue;
    out.push({
      taskId: t.taskId,
      name: t.name,
      status,
      assigneeId: t.assigneeId,
      daysOverdue,
    });
  }
  out.sort((a, b) => {
    if (b.daysOverdue !== a.daysOverdue) return b.daysOverdue - a.daysOverdue;
    return a.name.localeCompare(b.name, 'ja');
  });
  return out;
}

/* -------------------------------------------------------------------- */
/*  D. Assignee workload                                                 */
/* -------------------------------------------------------------------- */

export interface AssigneeWorkloadRow {
  assigneeId: string | null;
  name: string;
  colorIndex: number | null;
  total: number;
  done: number;
  inProgress: number;
  notStarted: number;
  blocked: number;
  /** 0..100 — `done / (total - skipped)`. `0` when denominator is zero. */
  donePercent: number;
}

/**
 * Group the current phase's tasks by assignee. Unassigned tasks land in
 * a single synthetic bucket (`assigneeId === null`). Sorted with the
 * highest total first so the heaviest workload reads at the top.
 */
export function computeAssigneeWorkload(
  tasks: readonly TaskDoc[],
  executions: readonly ExecutionDoc[],
  run: RunDoc | null,
  assignees: readonly AssigneeDoc[],
): AssigneeWorkloadRow[] {
  const execByTaskId = new Map<string, ExecutionDoc>();
  for (const e of executions) {
    if (!run || e.runId === run.runId) execByTaskId.set(e.taskId, e);
  }
  const assigneeById = new Map<string, AssigneeDoc>();
  for (const a of assignees) assigneeById.set(a.assigneeId, a);

  // `null` key represents the unassigned bucket.
  const buckets = new Map<string | null, AssigneeWorkloadRow>();
  const ensure = (id: string | null): AssigneeWorkloadRow => {
    const existing = buckets.get(id);
    if (existing) return existing;
    const doc = id ? assigneeById.get(id) ?? null : null;
    const row: AssigneeWorkloadRow = {
      assigneeId: id,
      name: doc?.name ?? (id ? '不明な担当者' : '未割当'),
      colorIndex: doc?.colorIndex ?? null,
      total: 0,
      done: 0,
      inProgress: 0,
      notStarted: 0,
      blocked: 0,
      donePercent: 0,
    };
    buckets.set(id, row);
    return row;
  };

  for (const t of tasks) {
    const row = ensure(t.assigneeId ?? null);
    const status: ExecutionStatus = execByTaskId.get(t.taskId)?.status ?? 'not_started';
    if (status === 'skipped') continue;
    row.total += 1;
    switch (status) {
      case 'done':
        row.done += 1;
        break;
      case 'in_progress':
        row.inProgress += 1;
        break;
      case 'not_started':
        row.notStarted += 1;
        break;
      case 'blocked':
        row.blocked += 1;
        break;
    }
  }
  for (const row of buckets.values()) {
    row.donePercent = row.total > 0 ? Math.round((row.done / row.total) * 100) : 0;
  }
  return Array.from(buckets.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return a.name.localeCompare(b.name, 'ja');
  });
}

/* -------------------------------------------------------------------- */
/*  E. Area progress                                                     */
/* -------------------------------------------------------------------- */

export interface AreaProgressRow {
  /** Area id (`WbsCatalogOption.id`), or `null` for unassigned. */
  areaId: string | null;
  label: string;
  total: number;
  done: number;
  inProgress: number;
  notStarted: number;
  blocked: number;
  donePercent: number;
}

/**
 * Stack-bar friendly aggregation by 領域. 領域 is multi-value
 * (`TaskDoc.areaIds`, read via {@link readTaskAreaIds}): a task that
 * belongs to several areas (e.g. 会計 + システム) counts toward EACH of
 * those buckets, so the per-area totals deliberately overlap and can sum
 * to more than the task count. Tasks with no area roll up under a single
 * "未設定" bucket. Areas declared in the project's `wbsCatalog.area` list
 * but with zero tasks are omitted — the chart would otherwise be
 * all-zero rows on fresh projects.
 */
export function computeAreaProgress(
  tasks: readonly TaskDoc[],
  executions: readonly ExecutionDoc[],
  run: RunDoc | null,
  catalog: readonly WbsCatalogOption[],
): AreaProgressRow[] {
  const labelById = new Map<string, string>();
  for (const o of catalog) labelById.set(o.id, o.label);

  const execByTaskId = new Map<string, ExecutionDoc>();
  for (const e of executions) {
    if (!run || e.runId === run.runId) execByTaskId.set(e.taskId, e);
  }

  const buckets = new Map<string | null, AreaProgressRow>();
  const ensure = (id: string | null): AreaProgressRow => {
    const existing = buckets.get(id);
    if (existing) return existing;
    const row: AreaProgressRow = {
      areaId: id,
      label: id ? labelById.get(id) ?? id : '未設定',
      total: 0,
      done: 0,
      inProgress: 0,
      notStarted: 0,
      blocked: 0,
      donePercent: 0,
    };
    buckets.set(id, row);
    return row;
  };

  for (const t of tasks) {
    const status: ExecutionStatus = execByTaskId.get(t.taskId)?.status ?? 'not_started';
    if (status === 'skipped') continue;
    // Expand the task into one bucket per assigned area; a multi-area
    // task counts toward each. No area → the single "未設定" bucket.
    const areaIds = readTaskAreaIds(t);
    const ids: (string | null)[] = areaIds.length > 0 ? areaIds : [null];
    for (const id of ids) {
      const row = ensure(id);
      row.total += 1;
      switch (status) {
        case 'done':
          row.done += 1;
          break;
        case 'in_progress':
          row.inProgress += 1;
          break;
        case 'not_started':
          row.notStarted += 1;
          break;
        case 'blocked':
          row.blocked += 1;
          break;
      }
    }
  }
  for (const row of buckets.values()) {
    row.donePercent = row.total > 0 ? Math.round((row.done / row.total) * 100) : 0;
  }
  return Array.from(buckets.values()).sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return a.label.localeCompare(b.label, 'ja');
  });
}

/* -------------------------------------------------------------------- */
/*  F. Recent activity sparkline                                         */
/* -------------------------------------------------------------------- */

export interface ActivityBucket {
  /** Local-midnight date for the bucket (oldest first). */
  day: Date;
  count: number;
}

/**
 * Bucket the project's recent activity log into per-day counts spanning
 * the last `days` days inclusive of today. We only count high-signal
 * status transitions (execution / wbs_item / task) so the sparkline
 * reflects "work moved" rather than ambient noise like setting tweaks.
 */
export function computeRecentActivity(
  logs: readonly ActivityLogDoc[],
  now: Date,
  days = 7,
): ActivityBucket[] {
  const today = startOfDay(now);
  const buckets: ActivityBucket[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets.push({ day: d, count: 0 });
  }
  const firstMillis = buckets[0]!.day.getTime();
  const lastMillis = endOfDay(buckets[buckets.length - 1]!.day).getTime();
  const SIGNAL_ACTIONS = new Set<string>([
    'execution.status_change',
    'execution.create',
    'execution.update',
    'task.create',
    'task.update',
    'wbs_item.status_change',
    'wbs_item.progress_change',
  ]);
  for (const log of logs) {
    if (!SIGNAL_ACTIONS.has(log.action)) continue;
    const m = log.createdAt?.toMillis?.();
    if (m == null) continue;
    if (m < firstMillis || m > lastMillis) continue;
    const dayIndex = Math.floor((startOfDay(new Date(m)).getTime() - firstMillis) / MS_PER_DAY);
    if (dayIndex >= 0 && dayIndex < buckets.length) {
      buckets[dayIndex]!.count += 1;
    }
  }
  return buckets;
}

/* -------------------------------------------------------------------- */
/*  G. Warnings (cycles + unassigned)                                    */
/* -------------------------------------------------------------------- */

export interface DashboardWarnings {
  cycleTaskCount: number;
  unassignedCount: number;
}

export function computeWarnings(tasks: readonly TaskDoc[]): DashboardWarnings {
  let cycleTaskCount = 0;
  let unassignedCount = 0;
  for (const t of tasks) {
    if (t.cycleDetected) cycleTaskCount += 1;
    if (!t.assigneeId) unassignedCount += 1;
  }
  return { cycleTaskCount, unassignedCount };
}

/* -------------------------------------------------------------------- */
/*  H. This week's due + undone WBS tasks (phase → backlog → assignee)  */
/* -------------------------------------------------------------------- */

/**
 * Rows fed to the dashboard's top-of-page "今週期限のタスク" section.
 * Sourced from `WbsItemDoc` (kind === 'task') rather than migration
 * executions — the section is a PM view over the whole WBS, not phase
 * execution progress.
 */
export interface ThisWeekDueTaskRow {
  itemId: string;
  name: string;
  plannedEnd: Date;
  backlogIssueKey: string;
  status: WbsItemStatus;
  assigneeId: string | null;
}

export interface ThisWeekDueAssigneeGroup {
  /** `null` = 未割当 bucket. */
  assigneeId: string | null;
  rows: ThisWeekDueTaskRow[];
}

export interface ThisWeekDueProjectGroup {
  backlogProjectKey: string;
  assigneeGroups: ThisWeekDueAssigneeGroup[];
}

export interface ThisWeekDuePhaseGroup {
  /**
   * The phase `itemId` this group aggregates under, or `null` for tasks
   * whose parent chain does not resolve to a `kind === 'phase'` row.
   */
  phaseItemId: string | null;
  phaseName: string;
  /**
   * Parent phase `sortKey` — used as the primary phase order. `null` for
   * the "(フェーズ外)" bucket, which sorts last.
   */
  phaseSortKey: string | null;
  projectGroups: ThisWeekDueProjectGroup[];
}

/** Monday 00:00 of the week containing `now` (local time). */
function startOfMondayWeek(now: Date): Date {
  const d = startOfDay(now);
  const dow = d.getDay(); // 0 = Sunday .. 6 = Saturday
  // Days back to Monday: Sunday → 6, Monday → 0, Tuesday → 1, ...
  const daysBack = dow === 0 ? 6 : dow - 1;
  d.setDate(d.getDate() - daysBack);
  return d;
}

/** Sunday 23:59:59.999 of the week containing `now` (local time). */
function endOfSundayWeek(now: Date): Date {
  const start = startOfMondayWeek(now);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Group this-week-due, undone WBS tasks by parent phase → Backlog project
 * key → assignee.
 *
 * Filters (all must hold):
 *   - `kind === 'task'` (milestones/phases excluded)
 *   - `status ∉ { 'done', 'cancelled' }`  (`blocked` is kept)
 *   - `plannedEnd` set and within [Mon 00:00, Sun 23:59.999] of the
 *     week containing `now`
 *   - `primaryBacklogProjectKey(item)` non-null (Backlog 未連携 rows are
 *     excluded — the section is Backlog-scoped by product intent)
 *
 * Sort order:
 *   phases          — parent phase `sortKey` asc, ties by `phaseItemId`.
 *                     The "(フェーズ外)" bucket (phaseItemId === null)
 *                     sorts LAST regardless.
 *   project groups  — `backlogProjectKey` ascending (localeCompare).
 *   assignee groups — `assigneeId` ascending; unassigned (null) LAST.
 *                     The component may re-sort by resolved name.
 *   rows            — `plannedEnd` asc, ties by `name` (ja locale).
 *
 * Pure; deterministic given the inputs.
 */
export function computeThisWeekDueByPhase(
  items: readonly WbsItemDoc[],
  now: Date,
): ThisWeekDuePhaseGroup[] {
  const weekStart = startOfMondayWeek(now).getTime();
  const weekEnd = endOfSundayWeek(now).getTime();

  const itemById = new Map<string, WbsItemDoc>();
  for (const it of items) itemById.set(it.itemId, it);

  const resolvePhase = (
    task: WbsItemDoc,
  ): { phaseItemId: string | null; phaseName: string; phaseSortKey: string | null } => {
    // Walk parent chain to the nearest ancestor with kind === 'phase'.
    // A defensive step cap prevents pathological cycles from hanging the
    // render (parentItemId edges should be a tree, but guard anyway).
    let cursor: WbsItemDoc | undefined = task;
    for (let i = 0; i < 32; i++) {
      const parentId: string | null = cursor?.parentItemId ?? null;
      if (!parentId) break;
      const parent = itemById.get(parentId);
      if (!parent) break;
      if (parent.kind === 'phase') {
        return {
          phaseItemId: parent.itemId,
          phaseName: parent.name,
          phaseSortKey: parent.sortKey,
        };
      }
      cursor = parent;
    }
    return { phaseItemId: null, phaseName: '(フェーズ外)', phaseSortKey: null };
  };

  interface AssigneeBucket {
    assigneeId: string | null;
    rows: ThisWeekDueTaskRow[];
  }
  interface ProjectBucket {
    backlogProjectKey: string;
    assigneesByKey: Map<string | null, AssigneeBucket>;
  }
  interface PhaseBucket {
    phaseItemId: string | null;
    phaseName: string;
    phaseSortKey: string | null;
    projectsByKey: Map<string, ProjectBucket>;
  }

  const phasesByKey = new Map<string, PhaseBucket>();
  const phaseKey = (id: string | null): string => id ?? '__no_phase__';

  for (const item of items) {
    if (item.kind !== 'task') continue;
    if (item.status === 'done' || item.status === 'cancelled') continue;
    if (!item.plannedEnd) continue;
    const plannedEnd = item.plannedEnd.toDate();
    const t = plannedEnd.getTime();
    if (t < weekStart || t > weekEnd) continue;
    const projectKey = primaryBacklogProjectKey(item);
    if (!projectKey) continue;
    const backlogIssueKey = item.backlogIssueKeys[0];
    if (!backlogIssueKey) continue;

    const phase = resolvePhase(item);
    const pKey = phaseKey(phase.phaseItemId);
    let phaseBucket = phasesByKey.get(pKey);
    if (!phaseBucket) {
      phaseBucket = {
        phaseItemId: phase.phaseItemId,
        phaseName: phase.phaseName,
        phaseSortKey: phase.phaseSortKey,
        projectsByKey: new Map(),
      };
      phasesByKey.set(pKey, phaseBucket);
    }

    let projectBucket = phaseBucket.projectsByKey.get(projectKey);
    if (!projectBucket) {
      projectBucket = { backlogProjectKey: projectKey, assigneesByKey: new Map() };
      phaseBucket.projectsByKey.set(projectKey, projectBucket);
    }

    const assigneeId: string | null = item.assigneeId ?? null;
    let assigneeBucket = projectBucket.assigneesByKey.get(assigneeId);
    if (!assigneeBucket) {
      assigneeBucket = { assigneeId, rows: [] };
      projectBucket.assigneesByKey.set(assigneeId, assigneeBucket);
    }

    assigneeBucket.rows.push({
      itemId: item.itemId,
      name: item.name,
      plannedEnd,
      backlogIssueKey,
      status: item.status,
      assigneeId,
    });
  }

  const sortedPhases: ThisWeekDuePhaseGroup[] = Array.from(phasesByKey.values()).map(
    (phase) => {
      const projectGroups: ThisWeekDueProjectGroup[] = Array.from(
        phase.projectsByKey.values(),
      )
        .map((project) => {
          const assigneeGroups: ThisWeekDueAssigneeGroup[] = Array.from(
            project.assigneesByKey.values(),
          )
            .map((bucket) => ({
              assigneeId: bucket.assigneeId,
              rows: [...bucket.rows].sort((a, b) => {
                if (a.plannedEnd.getTime() !== b.plannedEnd.getTime()) {
                  return a.plannedEnd.getTime() - b.plannedEnd.getTime();
                }
                return a.name.localeCompare(b.name, 'ja');
              }),
            }))
            .sort((a, b) => {
              // Unassigned (null) sorts last so named assignees lead each project.
              if (a.assigneeId == null && b.assigneeId != null) return 1;
              if (a.assigneeId != null && b.assigneeId == null) return -1;
              if (a.assigneeId == null && b.assigneeId == null) return 0;
              return (a.assigneeId as string).localeCompare(b.assigneeId as string);
            });
          return { backlogProjectKey: project.backlogProjectKey, assigneeGroups };
        })
        .sort((a, b) => a.backlogProjectKey.localeCompare(b.backlogProjectKey));

      return {
        phaseItemId: phase.phaseItemId,
        phaseName: phase.phaseName,
        phaseSortKey: phase.phaseSortKey,
        projectGroups,
      };
    },
  );

  sortedPhases.sort((a, b) => {
    // "(フェーズ外)" always last.
    if (a.phaseSortKey == null && b.phaseSortKey != null) return 1;
    if (a.phaseSortKey != null && b.phaseSortKey == null) return -1;
    if (a.phaseSortKey != null && b.phaseSortKey != null) {
      const cmp = a.phaseSortKey.localeCompare(b.phaseSortKey);
      if (cmp !== 0) return cmp;
    }
    // Ties: fall back to phaseItemId for stability.
    const ai = a.phaseItemId ?? '';
    const bi = b.phaseItemId ?? '';
    return ai.localeCompare(bi);
  });

  return sortedPhases;
}
